<template>
  <div class="toolbar">
    <User />
    <Cycle class="cycle" />
    <div class="toolbar__cycle">
      <span
        v-if="!userId && lockStatus === UNLOCKED"
        :class="{red: approvalStatus === REJECTED}"
      >{{ $t(`okr.toolbar.approvalStatus.${approvalStatus}`) }}</span>
      <span> {{ $t(`okr.toolbar.lockStatus.${lockStatus}`) }}</span>
    </div>
    <a-button
      id="weekly-button"
      @click="jumpToWeeklyMode"
    >
      {{ $t('weekly.weeklyMode') }}
    </a-button>
    <a-button
      v-if="canEdit"
      id="publish"
      type="primary"
      size="small"
      :disabled="!canPublish"
      :style="{ marginLeft: '32px' }"
      @click="beforePublish"
    >
      {{ $t('okr.toolbar.publish.publishButton') }}
    </a-button>
    <ToBeAlignedDot
      v-if="!canEdit"
      :style="{ marginLeft: '32px' }"
      :title="$t('okr.toolbar.needAlignHint')"
      :size="12"
      placement="left"
    />
    <a-modal
      v-model:visible="isPublishConfirmVisible"
      :confirm-loading="confirmLoading"
      @ok="publishOkr"
      @cancel="onCancel"
    >
      <template v-if="notifyUserObj">
        <p>
          {{ $t('okr.toolbar.publish.description') }}
        </p>
        <a-checkbox
          v-model:checked="doesNotify"
        >
          {{ $t('okr.toolbar.publish.notifyAssociated') }}
        </a-checkbox>
        <template v-if="doesNotify">
          <div
            v-for="notifyUserCol in notifyUserCols"
            :key="notifyUserCol.key"
          >
            {{ notifyUserCol.title }}：
            <template v-if="notifyUserObj[notifyUserCol.key] && notifyUserObj[notifyUserCol.key].length > 0">
              <a-tag
                v-for="name in notifyUserObj[notifyUserCol.key]"
                :key="name"
              >
                {{ name }}
              </a-tag>
            </template>
            <template v-else>
              -
            </template>
          </div>
        </template>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { REJECTED } from '@/constants/approval-status'
import { UNLOCKED } from '@/constants/lock-status'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import { publish, andyPublish } from '@/api/okr'
import { getNotifyUsers } from '@/graphql/okr'
import User from './User'
import Cycle from '@/components/Cycle'
import ToBeAlignedDot from '@/components/ToBeAlignedDot'
import { useRoute, useRouter } from 'vue-router'

export default {
  components: {
    User,
    Cycle,
    ToBeAlignedDot
  },
  props: {
    okrListId: {
      type: Number,
      default: undefined
    },
    canEdit: {
      type: Boolean,
      required: true
    },
    canPublish: {
      type: Boolean,
      required: true
    },
    approvalStatus: {
      type: Number,
      default: 0
    },
    weightAlarm: {
      type: Boolean,
      required: true
    },
    organizationalSkillsOrPersonalDevelopmentWarn: {
      type: Boolean,
      required: true
    },
    isPerformanceLeader: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'okr-published',
    'setWeightAlarmStyleTrue'
  ],
  setup (props, { emit }) {
    const store = useStore()
    const { t } = useI18n()
    const doesNotify = ref(true)
    const router = useRouter()
    const route = useRoute()
    const userId = route.params.userId && +route.params.userId
    const confirmLoading = ref(false)
    const notifyUserCols = computed(() => [
      {
        title: t('layout.menu.leaders'),
        key: 'leaders'
      },
      {
        title: t('layout.menu.subordinates'),
        key: 'departmentUsers'
      },
      {
        title: t('okr.toolbar.publish.followed'),
        key: 'followedByUsers'
      },
      {
        title: t('okr.toolbar.publish.alignedToPersons'),
        key: 'linkToUsers'
      },
      {
        title: t('okr.toolbar.publish.beAlignedFromPersons'),
        key: 'linkedFromUsers'
      },
      {
        title: t('okr.toolbar.publish.mentioned'),
        key: 'mentionUsers'
      }
    ])
    const isPublishConfirmVisible = ref(false)
    const notifyUserObj = ref(null)

    function beforePublish () {
      if (props.weightAlarm) {
        emit('setWeightAlarmStyleTrue', true)
        message.warning(t('okr.toolbar.publish.weightErrorMessage'))
        return
      }
      if (props.organizationalSkillsOrPersonalDevelopmentWarn) {
        message.warning(t(`okr.toolbar.publish.${props.isPerformanceLeader ? 'organizationalSkillsErrorMessage' : 'personalDevelopmentErrorMessage'}`))
        return
      }
      getNotifyUsers(props.okrListId).then(notifyUserObjRaw => {
        const notifyUserObjTemp = Object.keys(notifyUserObjRaw).reduce((obj, key) => {
          obj[key] = notifyUserObjRaw[key] ? notifyUserObjRaw[key].split(', ') : []
          return obj
        }, {})
        notifyUserObjTemp.departmentUsers = store.state.user.departmentUserIds.map(user => user.name)
        notifyUserObj.value = notifyUserObjTemp
        isPublishConfirmVisible.value = true
      })
    }

    function publishOkr () {
      confirmLoading.value = true
      if (store.state.user.jobNumber !== '10001') {
        publish(props.okrListId, doesNotify.value)
          .then(() => {
            confirmLoading.value = false
            isPublishConfirmVisible.value = false
            reset()
            message.success(t('okr.toolbar.publish.successMessage'), 5)
            emit('okr-published')
          }).catch(e => {
            message.error(t('okr.toolbar.publish.errorMessage'), 5)
            confirmLoading.value = false
          })
      } else {
        andyPublish(props.okrListId, doesNotify.value)
          .then(() => {
            confirmLoading.value = false
            isPublishConfirmVisible.value = false
            reset()
            message.success(t('okr.toolbar.publish.successMessage'), 5)
            emit('okr-published')
          }).catch(e => {
            message.error(t('okr.toolbar.publish.errorMessage'), 5)
            confirmLoading.value = false
          })
      }
    }

    function onCancel () {
      reset()
    }

    function reset () {
      notifyUserObj.value = null
    }

    const lockStatus = computed(() => store.getters.lockStatus)

    const jumpToWeeklyMode = () => {
      if (userId) {
        router.push(`/okr-week/${userId}`)
      } else {
        router.push('/okr-week')
      }
    }

    return {
      doesNotify,
      isPublishConfirmVisible,
      notifyUserCols,
      notifyUserObj,
      beforePublish,
      publishOkr,
      onCancel,
      REJECTED,
      userId,
      confirmLoading,
      lockStatus,
      UNLOCKED,
      jumpToWeeklyMode
    }
  }
}
</script>

<style lang="less" scoped>
.toolbar {
  position: sticky;
  top: 48px;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 -24px;
  padding: 16px 24px 8px;
  background-color: #F8F9FF;

  &__cycle {
    flex-grow: 1;
    text-align: center;
  }
}
.cycle {
  margin-left: 40px;
}
.red {
  color: red;
}
</style>
